/**
 * CHANGELOG.md (Keep a Changelog, at the repo root) read into releases for the
 * Changelog view. Each release is a `## [x.y.z] — YYYY-MM-DD` heading and the
 * markdown under it; anything before the first one (the title, the intro) and a
 * heading that is not a version (`## [Unreleased]`) are left out.
 */
export type Release = { version: string; date: string | null; body: string };

const HEADING = /^##\s+\[?v?(\d+\.\d+\.\d+(?:[-+][0-9A-Za-z.-]+)?)\]?\s*(?:[—–-]\s*(\S.*?))?\s*$/;

export function parseChangelog(text: string): Release[] {
  const releases: Release[] = [];
  let current: { version: string; date: string | null; lines: string[] } | null = null;
  const close = () => {
    if (current) releases.push({ version: current.version, date: current.date, body: current.lines.join("\n").trim() });
  };
  for (const line of text.replace(/\r\n?/g, "\n").split("\n")) {
    if (/^##\s/.test(line)) {
      close();
      const match = HEADING.exec(line);
      current = match ? { version: match[1]!, date: match[2] ?? null, lines: [] } : null;
      continue;
    }
    current?.lines.push(line);
  }
  close();
  for (const release of releases) release.body = unwrap(release.body);
  return releases.sort((a, b) => compareVersions(b.version, a.version));
}

const BLOCK_START = /^\s*(?:[-*+]\s|\d+[.)]\s|#|>|\||```)/;

/**
 * The file is wrapped at 120 columns for reading in git, and the app's markdown keeps
 * a newline as a line break, so a line that only continues the one above (not blank,
 * not a list item, heading, quote, table row or fence) is joined back onto it.
 */
export function unwrap(body: string): string {
  const out: string[] = [];
  let fenced = false;
  for (const line of body.split("\n")) {
    const previous = out.length ? out[out.length - 1]! : "";
    if (/^\s*```/.test(line)) fenced = !fenced;
    if (!fenced && line.trim() && previous.trim() && !BLOCK_START.test(line) && !/^\s*(?:#|\||```)/.test(previous)) {
      out[out.length - 1] = `${previous.trimEnd()} ${line.trim()}`;
    } else {
      out.push(line);
    }
  }
  return out.join("\n");
}

/** Semver order of the numeric parts; a pre-release sorts under its release. */
export function compareVersions(a: string, b: string): number {
  const parts = (v: string) => v.split(/[-+]/)[0]!.split(".").map(Number);
  const [pa, pb] = [parts(a), parts(b)];
  for (let i = 0; i < 3; i++) if (pa[i] !== pb[i]) return (pa[i] ?? 0) - (pb[i] ?? 0);
  return (a.includes("-") ? 0 : 1) - (b.includes("-") ? 0 : 1);
}
